import React, {useCallback, useContext, useLayoutEffect, useMemo, useRef, useState} from "react";
import {
    DataGridColumnPinnedTypes,
    DataGridControllerContext,
    DataGridMiscContext,
    DataGridScrollContext,
    DataGridStateContext,
} from "../../../index";
import classnames from "classnames";
import DataGridHeaderCell from "./cell";
import {DataGridExoticColumnFields} from "../../../models";
import {ReactSortable} from "react-sortablejs";
import DataGridUtils from "../../../core/services/utils";
import {resolveDataGridVirtualization} from "../../../core/services/utils/virtualization";
import {useColumnVirtualizer} from "../../../../../ui/hooks/use-row-virtualizer";

const DataGridHeader = ({layoutRectWidth}) => {
    const misc = useContext(DataGridMiscContext);
    const {columnsEnabled} = resolveDataGridVirtualization(misc);
    const {columns} = useContext(DataGridStateContext);
    const {classNames} = useContext(DataGridMiscContext);
    const {getLayoutScrollParent} = useContext(DataGridScrollContext);
    const dataGridApi = useContext(DataGridControllerContext);
    const [dragging, setDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [sortedColumns, setSortedColumns] = useState(
        columns.map(e => ({
            id: e.name,
            filtered: e.pinned || !e.reorderable,
        })),
    );
    /**@type {React.MutableRefObject<HTMLTableSectionElement>}*/
    const containerRef = useRef();
    const initialRender = useRef(true);

    const _visibleColumns = useMemo(() =>
        columns.filter(e =>
            e.visible &&
            e.name !== DataGridExoticColumnFields.detailedPanel,
        ), [columns]);

    const visibleColumns = useMemo(() => {
        return DataGridUtils.getVisibleColumnsCalculatedWidths(_visibleColumns, layoutRectWidth);
    }, [_visibleColumns, layoutRectWidth]);

    const getColWidth = useCallback((c) => Math.max(c.width?.size ?? 0, c.width?.minWidth ?? 0), []);
    const isPinnedLeft = useCallback((c) => c.pinnedType === DataGridColumnPinnedTypes.left, []);
    const isPinnedRight = useCallback((c) => c.pinnedType === DataGridColumnPinnedTypes.right, []);

    const colV = useColumnVirtualizer({
        enabled: columnsEnabled,
        columns: visibleColumns,
        getScrollParent: getLayoutScrollParent,
        getColumnWidth: getColWidth,
        isPinnedLeft,
        isPinnedRight,
        overscanPx: dragging ? 0 : 240,
    });

    const leftPinnedWithOffsets = useMemo(() => {
        let acc = 0;
        return colV.leftPinned.map((c) => {
            const off = acc;
            acc += getColWidth(c);
            return {...c, pinned: true, pinnedType: DataGridColumnPinnedTypes.left, leftPinOffset: off};
        });
    }, [colV.leftPinned, getColWidth]);

    const rightPinnedWithOffsets = useMemo(() => {
        let acc = 0;
        const rev = [...colV.rightPinned].reverse().map((c) => {
            const off = acc;
            acc += getColWidth(c);
            return {...c, pinned: true, pinnedType: DataGridColumnPinnedTypes.right, rightPinOffset: off};
        });
        return rev.reverse();
    }, [colV.rightPinned, getColWidth]);

    const availableWidth = useMemo(() => {
        return visibleColumns?.reduce((p, c) => p + Math.max(c.width.size, c.width.minWidth), 0) ?? 0;
    }, [visibleColumns]);

    const visibleColumnKey = useMemo(
        () => visibleColumns.map((c) => c.name).join('|'),
        [visibleColumns],
    );

    useLayoutEffect(() => {
        const tableEl = containerRef.current?.parentElement;
        if (!tableEl) return;

        // Always sync inline width so it shrinks when columns are removed or column
        // virtualization is turned off (previously only the width attribute was set).
        tableEl.style.width = `${availableWidth}px`;
        if (columnsEnabled) {
            tableEl.removeAttribute('width');
        } else {
            tableEl.setAttribute('width', `${availableWidth}`);
        }
    }, [availableWidth, columnsEnabled]);

    useLayoutEffect(() => {
        const layout = getLayoutScrollParent();
        if (layout) {
            layout.scrollLeft = 0;
        }
    }, [visibleColumnKey, columnsEnabled, getLayoutScrollParent]);

    useLayoutEffect(() => {
        setSortedColumns(prevState => {
            const newSList = visibleColumns.map(e => ({
                id: e.name,
                filtered: e.pinned || !e.reorderable,
            }));
            const prevList = prevState.map(e => ({
                id: e.id,
                filtered: e.filtered,
            }));
            if (DataGridUtils.deepEqual(prevList, newSList)) {
                return prevState;
            }
            return newSList;
        });
    }, [visibleColumns]);

    const resizeColumnsByOffset = useCallback((column, offset) => {
        dataGridApi.resizeColumnsBy({
            [column.name]: offset,
        }, true);
    }, [dataGridApi]);

    const setColumnsOrders = useCallback((newList) => {
        if (initialRender.current) {
            return (initialRender.current = false) || void 0;
        }
        setSortedColumns((prevState) => {
            const newSortedColumns = DataGridUtils.sortColumnsByOrder(
                newList.map((column) => ({
                    ...column,
                    name: column.id,
                    order: undefined,
                })),
            ).map(e => ({id: e.id, filtered: e.filtered}));

            const prevSortedColumns = prevState.map(e => ({
                id: e.id,
                filtered: e.filtered,
            }));

            if (DataGridUtils.deepEqual(newSortedColumns, prevSortedColumns)) {
                return prevState;
            }
            return newSortedColumns;
        });
        dataGridApi.setAllColumnsOrder(newList.map(e => e.id), true);
    }, [dataGridApi]);

    const content = useMemo(() => {
        if (!columnsEnabled) {
            return visibleColumns?.map((column) => (
                <DataGridHeaderCell
                    key={column.name}
                    column={column}
                    onIsResizingChanged={setIsResizing}
                    resizeColumn={resizeColumnsByOffset}
                />
            ));
        }

        const cells = [];

        for (const column of leftPinnedWithOffsets) {
            cells.push(
                <DataGridHeaderCell
                    key={column.name}
                    column={column}
                    onIsResizingChanged={setIsResizing}
                    resizeColumn={resizeColumnsByOffset}
                />,
            );
        }

        if (colV.leftSpacerWidth > 0) {
            cells.push(
                <th
                    key="__left-spacer__"
                    className="data-grid-spacer-cell"
                    style={{width: colV.leftSpacerWidth, padding: 0, border: 0}}
                    aria-hidden
                />,
            );
        }

        for (const column of colV.visibleCenter) {
            cells.push(
                <DataGridHeaderCell
                    key={column.name}
                    column={column}
                    onIsResizingChanged={setIsResizing}
                    resizeColumn={resizeColumnsByOffset}
                />,
            );
        }

        if (colV.rightSpacerWidth > 0) {
            cells.push(
                <th
                    key="__right-spacer__"
                    className="data-grid-spacer-cell"
                    style={{width: colV.rightSpacerWidth, padding: 0, border: 0}}
                    aria-hidden
                />,
            );
        }

        for (const column of rightPinnedWithOffsets) {
            cells.push(
                <DataGridHeaderCell
                    key={column.name}
                    column={column}
                    onIsResizingChanged={setIsResizing}
                    resizeColumn={resizeColumnsByOffset}
                />,
            );
        }

        return cells;
    }, [
        columnsEnabled,
        visibleColumns,
        leftPinnedWithOffsets,
        rightPinnedWithOffsets,
        colV.visibleCenter,
        colV.leftSpacerWidth,
        colV.rightSpacerWidth,
        resizeColumnsByOffset,
    ]);

    const scrollFn = useCallback((offsetX, offsetY, originalEvent) => {
        const scrollThreshold = 100;
        const scrollSpeed = 15;

        const layout = columnsEnabled
            ? getLayoutScrollParent()
            : containerRef.current?.parentElement?.parentElement;

        if (!layout || !containerRef.current?.parentElement) return;

        let {left, right} = layout.getBoundingClientRect();

        const leftPinnedColumns = containerRef.current.parentElement.querySelectorAll('.data-grid-header-cell.left-pinned');
        if (leftPinnedColumns.length) {
            const lastLeftPinnedColumn = leftPinnedColumns[leftPinnedColumns.length - 1];
            left = lastLeftPinnedColumn.getBoundingClientRect().right;
        }

        const rightPinnedColumns = containerRef.current.parentElement.querySelectorAll('.data-grid-header-cell.right-pinned');
        if (rightPinnedColumns.length) {
            const firstRightPinnedColumn = rightPinnedColumns[0];
            right = firstRightPinnedColumn.getBoundingClientRect().left;
        }

        if (originalEvent.clientX < left + scrollThreshold) {
            layout.scrollBy({
                left: -scrollSpeed,
                behavior: columnsEnabled ? 'auto' : 'instant',
            });
        }
        if (originalEvent.clientX > right - scrollThreshold) {
            layout.scrollBy({
                left: scrollSpeed,
                behavior: columnsEnabled ? 'auto' : 'instant',
            });
        }
    }, [columnsEnabled, getLayoutScrollParent]);

    const sortableContainer = useMemo(() =>
        <ReactSortable
            tag={'tr'}
            list={sortedColumns}
            draggable={'.reorderable'}
            ignore={'.not-reorderable'}
            setList={setColumnsOrders}
            forceAutoScrollFallback
            scrollSensitivity={Number.MAX_SAFE_INTEGER}
            scrollFn={scrollFn}
            onStart={() => setDragging(true)}
            onEnd={() => setDragging(false)}
            dragClass={'data-grid-dragged-header-cell'}
        >
            {content}
        </ReactSortable>, [sortedColumns, setColumnsOrders, content, scrollFn]);

    return (
        <>
            <thead ref={containerRef} className={classnames(
                'data-grid-header',
                classNames.header,
                {'dragging-cell': !!dragging},
                {'resizing-cell': isResizing},
            )}>
            {sortableContainer}
            </thead>
        </>
    );
};

export default DataGridHeader;
